import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Pencil, Minus, ArrowRight, Circle, Eraser, Trash2, Save, RotateCcw,
  Smartphone, Monitor, Image, Eye, X, GripVertical, Download, AlertTriangle,
  MoreHorizontal, XCircle, CircleDot, Users
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pitch drawing functions
function drawPitch(ctx, w, h, style, viewMode, orientation) {
  const isGreen = style === 'green';
  const bg = isGreen ? '#2d8a4e' : '#ffffff';
  const line = isGreen ? '#ffffff' : '#1a472a';
  const lineW = 2;

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = line;
  ctx.lineWidth = lineW;
  ctx.fillStyle = line;

  const m = 10; // margin
  const pw = w - m * 2;
  const ph = h - m * 2;

  // Outer boundary
  ctx.strokeRect(m, m, pw, ph);

  if (orientation === 'horizontal') {
    // HORIZONTAL: goals on LEFT and RIGHT, center line vertical
    // pw = long side (105m), ph = short side (68m)
    const cx = m + pw / 2;
    const cy = m + ph / 2;

    // Center line (vertical)
    ctx.beginPath();
    ctx.moveTo(cx, m);
    ctx.lineTo(cx, m + ph);
    ctx.stroke();

    // Center circle
    ctx.beginPath();
    ctx.arc(cx, cy, pw * 0.087, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fill();

    // Penalty area dimensions (rotated)
    const paDepth = pw * 0.16;   // depth into pitch from goal line
    const paHeight = ph * 0.58;  // across the pitch (vertical span)
    const gaDepth = pw * 0.055;
    const gaHeight = ph * 0.27;
    const penSpotDist = pw * 0.105;
    const arcRadius = pw * 0.087;
    const dToLine = paDepth - penSpotDist;

    // LEFT penalty area (goal on left edge)
    ctx.strokeRect(m, cy - paHeight / 2, paDepth, paHeight);
    // LEFT goal area
    ctx.strokeRect(m, cy - gaHeight / 2, gaDepth, gaHeight);
    // LEFT penalty spot
    const leftPen = m + penSpotDist;
    ctx.beginPath();
    ctx.arc(leftPen, cy, 3, 0, Math.PI * 2);
    ctx.fill();
    // LEFT D — arc faces right, touches the 18-yard line
    // For horizontal: cos(θ) = dToLine / arcRadius at intersection
    const arcAngleH = Math.acos(Math.min(dToLine / arcRadius, 1));
    ctx.beginPath();
    ctx.arc(leftPen, cy, arcRadius, -arcAngleH, arcAngleH);
    ctx.stroke();

    // RIGHT penalty area (goal on right edge)
    ctx.strokeRect(m + pw - paDepth, cy - paHeight / 2, paDepth, paHeight);
    // RIGHT goal area
    ctx.strokeRect(m + pw - gaDepth, cy - gaHeight / 2, gaDepth, gaHeight);
    // RIGHT penalty spot
    const rightPen = m + pw - penSpotDist;
    ctx.beginPath();
    ctx.arc(rightPen, cy, 3, 0, Math.PI * 2);
    ctx.fill();
    // RIGHT D — arc faces left, touches the 18-yard line
    ctx.beginPath();
    ctx.arc(rightPen, cy, arcRadius, Math.PI - arcAngleH, Math.PI + arcAngleH);
    ctx.stroke();

    // Corner arcs
    ctx.beginPath(); ctx.arc(m, m, 12, 0, Math.PI * 0.5); ctx.stroke();
    ctx.beginPath(); ctx.arc(m + pw, m, 12, Math.PI * 0.5, Math.PI); ctx.stroke();
    ctx.beginPath(); ctx.arc(m, m + ph, 12, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(m + pw, m + ph, 12, Math.PI, Math.PI * 1.5); ctx.stroke();

    // Goals (left and right)
    const goalH = ph * 0.1;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(m - 8, cy - goalH / 2, 8, goalH);
    ctx.strokeRect(m + pw, cy - goalH / 2, 8, goalH);
    ctx.setLineDash([]);
  } else if (viewMode === 'halfD' || viewMode === 'halfA') {
    // HALF PITCH views — ph represents half the pitch, so double all ph-based proportions
    const cx = m + pw / 2;
    const isDefensive = viewMode === 'halfD';

    // Proportions doubled (full pitch uses ph*0.16, etc. but ph is now half)
    const paW = pw * 0.58;
    const paH = ph * 0.32;      // 0.16 * 2
    const gaW = pw * 0.27;
    const gaH = ph * 0.11;      // 0.055 * 2
    const penSpotDist = ph * 0.21; // 0.105 * 2
    const arcRadius = ph * 0.174;  // 0.087 * 2
    const circleR = ph * 0.24;    // 0.12 * 2
    const dToLine = paH - penSpotDist;
    const arcAngle = Math.asin(Math.min(dToLine / arcRadius, 1));

    if (isDefensive) {
      // HALF (D) — Defensive: goal at BOTTOM, center circle at TOP
      const bottom = m + ph;

      // Center line + half circle at top
      ctx.beginPath();
      ctx.arc(cx, m, circleR, 0, Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, m, 3, 0, Math.PI * 2);
      ctx.fill();

      // Penalty area at bottom
      ctx.strokeRect(cx - paW / 2, bottom - paH, paW, paH);
      // Goal area
      ctx.strokeRect(cx - gaW / 2, bottom - gaH, gaW, gaH);
      // Penalty spot
      const penSpot = bottom - penSpotDist;
      ctx.beginPath();
      ctx.arc(cx, penSpot, 3, 0, Math.PI * 2);
      ctx.fill();
      // D arc
      ctx.beginPath();
      ctx.arc(cx, penSpot, arcRadius, Math.PI + arcAngle, 2 * Math.PI - arcAngle);
      ctx.stroke();

      // Corner arcs at bottom
      ctx.beginPath();
      ctx.arc(m, bottom, 12, Math.PI * 1.5, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(m + pw, bottom, 12, Math.PI, Math.PI * 1.5);
      ctx.stroke();

      // Goal at bottom
      const goalW = pw * 0.1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(cx - goalW / 2, bottom, goalW, 8);
      ctx.setLineDash([]);
    } else {
      // HALF (A) — Attacking: goal at TOP, center circle at BOTTOM
      const top = m;
      const bottom = m + ph;

      // Center line + half circle at bottom
      ctx.beginPath();
      ctx.arc(cx, bottom, circleR, Math.PI, 2 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, bottom, 3, 0, Math.PI * 2);
      ctx.fill();

      // Penalty area at top
      ctx.strokeRect(cx - paW / 2, top, paW, paH);
      // Goal area
      ctx.strokeRect(cx - gaW / 2, top, gaW, gaH);
      // Penalty spot
      const penSpot = top + penSpotDist;
      ctx.beginPath();
      ctx.arc(cx, penSpot, 3, 0, Math.PI * 2);
      ctx.fill();
      // D arc
      ctx.beginPath();
      ctx.arc(cx, penSpot, arcRadius, arcAngle, Math.PI - arcAngle);
      ctx.stroke();

      // Corner arcs at top
      ctx.beginPath();
      ctx.arc(m, top, 12, 0, Math.PI * 0.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(m + pw, top, 12, Math.PI * 0.5, Math.PI);
      ctx.stroke();

      // Goal at top
      const goalW = pw * 0.1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(cx - goalW / 2, top - 8, goalW, 8);
      ctx.setLineDash([]);
    }
  } else {
    // VERTICAL FULL: goals top and bottom
    const cx = m + pw / 2;
    const cy = m + ph / 2;

    // Center line (horizontal)
    ctx.beginPath();
    ctx.moveTo(m, cy);
    ctx.lineTo(m + pw, cy);
    ctx.stroke();

    // Center circle
    ctx.beginPath();
    ctx.arc(cx, cy, ph * 0.12, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fill();

    // Top penalty area
    const paW = pw * 0.58;
    const paH = ph * 0.16;
    ctx.strokeRect(cx - paW / 2, m, paW, paH);
    // Top goal area
    const gaW = pw * 0.27;
    const gaH = ph * 0.055;
    ctx.strokeRect(cx - gaW / 2, m, gaW, gaH);
    // Top penalty spot
    const penSpotDist = ph * 0.105;
    const topPen = m + penSpotDist;
    ctx.beginPath();
    ctx.arc(cx, topPen, 3, 0, Math.PI * 2);
    ctx.fill();
    // Top D
    const arcRadius = ph * 0.087;
    const dToLine = paH - penSpotDist;
    const arcAngle = Math.asin(Math.min(dToLine / arcRadius, 1));
    ctx.beginPath();
    ctx.arc(cx, topPen, arcRadius, arcAngle, Math.PI - arcAngle);
    ctx.stroke();

    // Bottom penalty area
    ctx.strokeRect(cx - paW / 2, m + ph - paH, paW, paH);
    // Bottom goal area
    ctx.strokeRect(cx - gaW / 2, m + ph - gaH, gaW, gaH);
    // Bottom penalty spot
    const botPen = m + ph - penSpotDist;
    ctx.beginPath();
    ctx.arc(cx, botPen, 3, 0, Math.PI * 2);
    ctx.fill();
    // Bottom D
    ctx.beginPath();
    ctx.arc(cx, botPen, arcRadius, Math.PI + arcAngle, 2 * Math.PI - arcAngle);
    ctx.stroke();

    // Corner arcs
    ctx.beginPath(); ctx.arc(m, m, 12, 0, Math.PI * 0.5); ctx.stroke();
    ctx.beginPath(); ctx.arc(m + pw, m, 12, Math.PI * 0.5, Math.PI); ctx.stroke();
    ctx.beginPath(); ctx.arc(m, m + ph, 12, Math.PI * 1.5, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(m + pw, m + ph, 12, Math.PI, Math.PI * 1.5); ctx.stroke();

    // Goals
    const goalW = pw * 0.1;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(cx - goalW / 2, m - 8, goalW, 8);
    ctx.strokeRect(cx - goalW / 2, m + ph, goalW, 8);
    ctx.setLineDash([]);
  }
}

const TOOLS = [
  { id: 'freehand', icon: Pencil, label: 'Draw' },
  { id: 'line', icon: Minus, label: 'Line' },
  { id: 'dotted', icon: MoreHorizontal, label: 'Dotted' },
  { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
  { id: 'player', icon: Circle, label: 'Player' },
  { id: 'xmark', icon: XCircle, label: 'X Mark' },
  { id: 'omark', icon: CircleDot, label: 'O Mark' },
  { id: 'eraser', icon: Eraser, label: 'Eraser' },
];

const COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#f97316', label: 'Orange' },
  { value: '#22c55e', label: 'Green' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#ffffff', label: 'White' },
];

// Formation presets — rows defined from defensive (own goal) to attacking
// Each row is the count of players. GK is automatically the first marker.
// Coordinates derived as percentages so they scale with canvas size and orientation.
const FORMATIONS = {
  '4-3-3': { rows: [4, 3, 3], label: '4-3-3' },
  '4-4-2': { rows: [4, 4, 2], label: '4-4-2' },
  '3-5-2': { rows: [3, 5, 2], label: '3-5-2' },
};

// Compute (xPct, yPct) positions for an 11-player formation defending the
// "back" of the pitch (bottom in vertical, left in horizontal).
// Returns array of { xPct, yPct } in display order: GK, then back-to-front rows.
function buildFormationPositions(rows) {
  // Lane positions along the pitch length (from own goal -> opposition goal)
  // GK very close to own goal, then defenders / mids / attackers spread out.
  const laneY = [0.92, 0.78, 0.55, 0.30];
  const positions = [{ xPct: 0.5, yPct: laneY[0] }]; // GK
  rows.forEach((count, rowIdx) => {
    const y = laneY[rowIdx + 1];
    if (count === 1) {
      positions.push({ xPct: 0.5, yPct: y });
    } else {
      // Spread players from 0.13 to 0.87 across the pitch width
      const start = 0.13;
      const end = 0.87;
      for (let i = 0; i < count; i++) {
        const xPct = start + ((end - start) * i) / (count - 1);
        positions.push({ xPct, yPct: y });
      }
    }
  });
  return positions;
}

export default function TacticsBoard() {
  const [tool, setTool] = useState('freehand');
  const [color, setColor] = useState('#000000');
  const [pitchStyle, setPitchStyle] = useState('green');
  const [orientation, setOrientation] = useState('vertical');
  const [viewMode, setViewMode] = useState('full');
  const [playerMarkers, setPlayerMarkers] = useState([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [drawHistory, setDrawHistory] = useState([]);
  const [boards, setBoards] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewBoard, setViewBoard] = useState(null);
  const [boardName, setBoardName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() =>
    drawHistory.length > 0 || playerMarkers.length > 0,
    [drawHistory.length, playerMarkers.length]
  );
  const { showPrompt, confirmNavigation, cancelNavigation } = useUnsavedChanges(
    hasUnsavedChanges,
    'You have unsaved changes on your tactics board. Any drawings or markers will be lost.'
  );
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const pendingNavRef = useRef(null);

  // When the hook triggers a prompt, show our custom save-or-leave dialog
  useEffect(() => {
    if (showPrompt) {
      setShowSavePrompt(true);
    }
  }, [showPrompt]);

  const canvasRef = useRef(null);
  const pitchCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const isDrawing = useRef(false);
  const startPoint = useRef(null);
  const currentPath = useRef([]);
  const draggingMarker = useRef(null);
  const lastPointerPos = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  // Canvas dimensions — fixed pitch aspect ratios
  // Full: 420:600 (7:10), Half: 420:300 (7:5), Horizontal: 600:420 (10:7)
  const getCanvasSize = useCallback(() => {
    const container = containerRef.current;
    if (!container) return { w: 420, h: 600 };
    const cw = container.clientWidth;
    const maxH = window.innerHeight - 200;
    const isHalf = viewMode === 'halfD' || viewMode === 'halfA';
    if (orientation === 'horizontal') {
      // Landscape: width:height = 600:420 = 10:7
      let w = cw;
      let h = w * (420 / 600);
      if (h > maxH) {
        h = maxH;
        w = h * (600 / 420);
      }
      return { w, h };
    }
    if (isHalf) {
      // Half pitch: width:height = 420:300 = 7:5
      let w = cw;
      let h = w * (300 / 420);
      if (h > maxH) {
        h = maxH;
        w = h * (420 / 300);
      }
      return { w, h };
    }
    // Full portrait: width:height = 420:600 = 7:10
    let w = cw;
    let h = w * (600 / 420);
    if (h > maxH) {
      h = maxH;
      w = h * (420 / 600);
    }
    return { w, h };
  }, [orientation, viewMode]);

  // Redraw pitch
  const redrawPitch = useCallback(() => {
    const pitchCanvas = pitchCanvasRef.current;
    if (!pitchCanvas) return;
    const ctx = pitchCanvas.getContext('2d');
    const { w, h } = getCanvasSize();
    pitchCanvas.width = w;
    pitchCanvas.height = h;
    drawPitch(ctx, w, h, pitchStyle, viewMode, orientation);
  }, [pitchStyle, viewMode, orientation, getCanvasSize]);

  // Draw a single stroke on a canvas context
  const drawStroke = useCallback((ctx, stroke) => {
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.tool === 'eraser' ? 20 : 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([]);

    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
    }

    if (stroke.tool === 'freehand' || stroke.tool === 'eraser') {
      if (stroke.points.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      stroke.points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    } else if (stroke.tool === 'line') {
      ctx.beginPath();
      ctx.moveTo(stroke.start.x, stroke.start.y);
      ctx.lineTo(stroke.end.x, stroke.end.y);
      ctx.stroke();
    } else if (stroke.tool === 'dotted') {
      ctx.setLineDash([8, 6]);
      ctx.beginPath();
      ctx.moveTo(stroke.start.x, stroke.start.y);
      ctx.lineTo(stroke.end.x, stroke.end.y);
      ctx.stroke();
      ctx.setLineDash([]);
    } else if (stroke.tool === 'arrow') {
      const { start, end } = stroke;
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      const angle = Math.atan2(end.y - start.y, end.x - start.x);
      const headLen = 14;
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headLen * Math.cos(angle - Math.PI / 6), end.y - headLen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headLen * Math.cos(angle + Math.PI / 6), end.y - headLen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    } else if (stroke.tool === 'xmark') {
      const s = 12;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(stroke.pos.x - s, stroke.pos.y - s);
      ctx.lineTo(stroke.pos.x + s, stroke.pos.y + s);
      ctx.moveTo(stroke.pos.x + s, stroke.pos.y - s);
      ctx.lineTo(stroke.pos.x - s, stroke.pos.y + s);
      ctx.stroke();
    } else if (stroke.tool === 'omark') {
      const r = 12;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(stroke.pos.x, stroke.pos.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
  }, []);

  // Redraw all strokes from history — fires directly when drawHistory changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { w, h } = getCanvasSize();
    canvas.width = w;
    canvas.height = h;
    drawHistory.forEach(stroke => drawStroke(ctx, stroke));
    ctx.globalCompositeOperation = 'source-over';
  }, [drawHistory, getCanvasSize, drawStroke]);

  useEffect(() => { redrawPitch(); }, [redrawPitch]);

  // Redraw everything on resize
  useEffect(() => {
    const handleResize = () => {
      redrawPitch();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const { w, h } = getCanvasSize();
      canvas.width = w;
      canvas.height = h;
      drawHistory.forEach(stroke => drawStroke(ctx, stroke));
      ctx.globalCompositeOperation = 'source-over';
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [redrawPitch, drawHistory, getCanvasSize, drawStroke]);

  useEffect(() => { loadBoards(); }, []);

  const loadBoards = async () => {
    try {
      const res = await fetch(`${API}/tactics-board/list`, { headers });
      if (res.ok) {
        const data = await res.json();
        setBoards(data.boards);
      }
    } catch (e) {
      console.error('[TacticsBoard] loadBoards failed:', e);
    }
  };

  // Get canvas coordinates from event
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const handlePointerDown = (e) => {
    if (tool === 'player') {
      const pos = getPos(e);
      const num = playerCount + 1;
      setPlayerMarkers(prev => [...prev, { id: Date.now(), x: pos.x, y: pos.y, num, color }]);
      setPlayerCount(num);
      return;
    }

    if (tool === 'xmark' || tool === 'omark') {
      const pos = getPos(e);
      setDrawHistory(prev => [...prev, { tool, color, pos }]);
      return;
    }

    isDrawing.current = true;
    const pos = getPos(e);
    startPoint.current = pos;
    lastPointerPos.current = pos;
    currentPath.current = [pos];

    if (tool === 'freehand' || tool === 'eraser') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = tool === 'eraser' ? 20 : 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (tool === 'eraser') ctx.globalCompositeOperation = 'destination-out';
    }
  };

  const drawLinePreview = (ctx, start, end, currentTool, currentColor) => {
    ctx.strokeStyle = currentColor;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    if (currentTool === 'dotted') ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    if (currentTool === 'dotted') ctx.setLineDash([]);
    if (currentTool === 'arrow') {
      const angle = Math.atan2(end.y - start.y, end.x - start.x);
      const headLen = 14;
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headLen * Math.cos(angle - Math.PI / 6), end.y - headLen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headLen * Math.cos(angle + Math.PI / 6), end.y - headLen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    }
  };

  const handlePointerMove = (e) => {
    if (!isDrawing.current) return;
    const pos = getPos(e);
    lastPointerPos.current = pos;

    if (tool === 'freehand' || tool === 'eraser') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      currentPath.current.push(pos);
    } else if (tool === 'line' || tool === 'arrow' || tool === 'dotted') {
      // Show live preview
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const { w, h } = getCanvasSize();
      ctx.clearRect(0, 0, w, h);
      // Redraw existing strokes
      drawHistory.forEach(stroke => drawStroke(ctx, stroke));
      ctx.globalCompositeOperation = 'source-over';
      // Draw current preview
      drawLinePreview(ctx, startPoint.current, pos, tool, color);
    }
  };

  const handlePointerUp = (e) => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';

    if (tool === 'freehand' || tool === 'eraser') {
      if (currentPath.current.length > 1) {
        const points = [...currentPath.current];
        setDrawHistory(prev => [...prev, { tool, color, points }]);
      }
    } else if (tool === 'line' || tool === 'arrow' || tool === 'dotted') {
      const end = lastPointerPos.current || startPoint.current;
      if (end && startPoint.current) {
        setDrawHistory(prev => [...prev, { tool, color, start: startPoint.current, end }]);
      }
    }
    currentPath.current = [];
    lastPointerPos.current = null;
  };

  // Marker dragging
  const handleMarkerDown = (e, markerId) => {
    e.stopPropagation();
    e.preventDefault();
    draggingMarker.current = markerId;
    const handleMove = (ev) => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const x = (clientX - rect.left) * (canvas.width / rect.width);
      const y = (clientY - rect.top) * (canvas.height / rect.height);
      setPlayerMarkers(prev => prev.map(m => m.id === markerId ? { ...m, x, y } : m));
    };
    const handleUp = () => {
      draggingMarker.current = null;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
  };

  const clearBoard = () => {
    setDrawHistory([]);
    setPlayerMarkers([]);
    setPlayerCount(0);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const undoLast = () => {
    if (drawHistory.length > 0) {
      setDrawHistory(prev => prev.slice(0, -1));
    } else if (playerMarkers.length > 0) {
      setPlayerMarkers(prev => prev.slice(0, -1));
      setPlayerCount(prev => Math.max(0, prev - 1));
    }
  };

  // Auto-place 11 markers in the chosen formation (replaces existing markers)
  const applyFormation = (name) => {
    const f = FORMATIONS[name];
    if (!f) return;
    if (viewMode !== 'full') {
      toast.error('Formations are only available on the Full pitch view.');
      return;
    }
    const { w, h } = getCanvasSize();
    const positions = buildFormationPositions(f.rows);
    const ts = Date.now();
    const markers = positions.map((p, idx) => {
      // For horizontal orientation, the pitch length runs left->right; we keep
      // the team defending the LEFT side, so swap the percentage axes.
      const xPct = orientation === 'horizontal' ? (1 - p.yPct) : p.xPct;
      const yPct = orientation === 'horizontal' ? p.xPct : p.yPct;
      return {
        id: ts + idx,
        x: xPct * w,
        y: yPct * h,
        num: idx + 1,
        color,
      };
    });
    setPlayerMarkers(markers);
    setPlayerCount(markers.length);
    toast.success(`${f.label} formation applied`);
  };

  const generateImage = () => {
    const { w, h } = getCanvasSize();
    const merged = document.createElement('canvas');
    merged.width = w;
    merged.height = h;
    const ctx = merged.getContext('2d');
    // Draw pitch
    ctx.drawImage(pitchCanvasRef.current, 0, 0);
    // Draw strokes
    ctx.drawImage(canvasRef.current, 0, 0);
    // Draw player markers
    playerMarkers.forEach(m => {
      const r = 16;
      ctx.beginPath();
      ctx.arc(m.x, m.y, r, 0, Math.PI * 2);
      ctx.fillStyle = m.color;
      ctx.fill();
      ctx.strokeStyle = m.color === '#ffffff' ? '#000000' : '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.fillStyle = m.color === '#ffffff' ? '#000000' : '#ffffff';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(m.num, m.x, m.y);
    });
    return merged.toDataURL('image/png');
  };

  const handleSave = async () => {
    if (!boardName.trim()) { toast.error('Please enter a name'); return; }
    setSaving(true);
    try {
      const imageData = generateImage();
      const res = await fetch(`${API}/tactics-board/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: boardName.trim(),
          image_data: imageData,
          orientation,
          pitch_style: pitchStyle,
          view_mode: viewMode,
        }),
      });
      if (res.ok) {
        toast.success('Tactics board saved');
        setShowSaveDialog(false);
        setBoardName('');
        loadBoards();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const deleteBoard = async (id) => {
    try {
      await fetch(`${API}/tactics-board/${id}`, { method: 'DELETE', headers });
      toast.success('Deleted');
      loadBoards();
      if (viewBoard?.id === id) setShowViewDialog(false);
    } catch (e) {
      console.error('[TacticsBoard] delete board failed:', e);
      toast.error('Could not delete the board');
    }
  };

  const { w, h } = getCanvasSize();

  return (
    <Layout>
      <div className="space-y-4 max-w-5xl mx-auto" data-testid="tactics-board-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900" data-testid="page-title">Tactics Board</h1>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowGallery(!showGallery)} data-testid="gallery-toggle">
              <Image className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Saved ({boards.length})</span>
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 bg-white p-2 rounded-xl border border-gray-200 shadow-sm" data-testid="toolbar">
          {/* Tools */}
          <div className="flex gap-1" data-testid="tool-buttons">
            {TOOLS.map(t => (
              <button
                key={t.id}
                onClick={() => setTool(t.id)}
                className={`p-2 rounded-lg transition-colors ${tool === t.id ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500 hover:bg-gray-100'}`}
                title={t.label}
                data-testid={`tool-${t.id}`}
              >
                <t.icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          <div className="w-px h-6 bg-gray-200" />

          {/* Colors */}
          <div className="flex gap-1" data-testid="color-buttons">
            {COLORS.map(c => (
              <button
                key={c.value}
                onClick={() => setColor(c.value)}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c.value ? 'border-emerald-500 scale-110' : 'border-gray-300'}`}
                style={{ backgroundColor: c.value }}
                title={c.label}
                data-testid={`color-${c.label.toLowerCase()}`}
              />
            ))}
          </div>

          <div className="w-px h-6 bg-gray-200 hidden sm:block" />

          {/* Pitch controls */}
          <div className="flex gap-1" data-testid="pitch-controls">
            <button
              onClick={() => setPitchStyle(pitchStyle === 'green' ? 'white' : 'green')}
              className="px-2 py-1.5 text-xs rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="toggle-pitch-style"
            >
              {pitchStyle === 'green' ? 'White' : 'Green'}
            </button>
            <button
              onClick={() => setOrientation(orientation === 'vertical' ? 'horizontal' : 'vertical')}
              className="px-2 py-1.5 text-xs rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="toggle-orientation"
            >
              {orientation === 'vertical' ? <Monitor className="w-3.5 h-3.5 inline" /> : <Smartphone className="w-3.5 h-3.5 inline" />}
              <span className="ml-1">{orientation === 'vertical' ? 'Horiz' : 'Vert'}</span>
            </button>
            <button
              onClick={() => {
                const next = viewMode === 'full' ? 'halfD' : viewMode === 'halfD' ? 'halfA' : 'full';
                setViewMode(next);
              }}
              className="px-2 py-1.5 text-xs rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="toggle-view-mode"
            >
              {viewMode === 'full' ? 'Full' : viewMode === 'halfD' ? 'Half (D)' : 'Half (A)'}
            </button>
          </div>

          {/* Formation preset */}
          <div className="flex items-center gap-1" data-testid="formation-controls">
            <Users className="w-3.5 h-3.5 text-gray-400" />
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  applyFormation(e.target.value);
                  e.target.value = '';
                }
              }}
              disabled={viewMode !== 'full'}
              className="h-8 px-2 text-xs rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors border-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="formation-select"
              title={viewMode === 'full' ? 'Apply a formation preset (11 players)' : 'Switch to Full pitch view to use formations'}
            >
              <option value="">Formation</option>
              {Object.keys(FORMATIONS).map(key => (
                <option key={key} value={key}>{FORMATIONS[key].label}</option>
              ))}
            </select>
          </div>

          <div className="flex-1" />

          {/* Actions */}
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={undoLast} data-testid="undo-btn" title="Undo">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={clearBoard} data-testid="clear-btn">
              <Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Clear</span>
            </Button>
            <Button size="sm" onClick={() => setShowSaveDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-btn">
              <Save className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>

        {/* Canvas area */}
        <div ref={containerRef} className="w-full">
          <div
            className="relative bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-sm touch-none mx-auto"
            style={{ width: w, height: h }}
            data-testid="canvas-container"
          >
          <canvas
            ref={pitchCanvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ touchAction: 'none' }}
          />
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ touchAction: 'none', cursor: tool === 'eraser' ? 'cell' : tool === 'player' ? 'crosshair' : 'crosshair' }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={() => { if (isDrawing.current) handlePointerUp({}); }}
            onTouchStart={(e) => { e.preventDefault(); handlePointerDown(e); }}
            onTouchMove={(e) => { e.preventDefault(); handlePointerMove(e); }}
            onTouchEnd={(e) => { e.preventDefault(); handlePointerUp(e); }}
            data-testid="drawing-canvas"
          />
          {/* Player markers */}
          {playerMarkers.map(m => {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const rect = canvas.getBoundingClientRect();
            const scaleX = rect.width / canvas.width;
            const scaleY = rect.height / canvas.height;
            return (
              <div
                key={m.id}
                className="absolute flex items-center justify-center rounded-full cursor-grab active:cursor-grabbing select-none"
                style={{
                  left: m.x * scaleX - 16,
                  top: m.y * scaleY - 16,
                  width: 32, height: 32,
                  backgroundColor: m.color,
                  border: `2px solid ${m.color === '#ffffff' ? '#000' : '#fff'}`,
                  color: m.color === '#ffffff' ? '#000' : '#fff',
                  fontSize: 14, fontWeight: 'bold',
                  touchAction: 'none',
                  zIndex: 10,
                }}
                onMouseDown={(e) => handleMarkerDown(e, m.id)}
                onTouchStart={(e) => handleMarkerDown(e, m.id)}
                data-testid={`marker-${m.num}`}
              >
                {m.num}
              </div>
            );
          })}
          </div>
        </div>

        {/* Saved boards gallery */}
        {showGallery && (
          <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="boards-gallery">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">Saved Boards</h2>
              <button onClick={() => setShowGallery(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            {boards.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No saved boards yet</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {boards.map(b => (
                  <div key={b.id} className="group relative rounded-lg overflow-hidden border border-gray-200 hover:border-emerald-300 transition-colors">
                    <img
                      src={b.image_data}
                      alt={b.name}
                      className="w-full aspect-[3/4] object-cover cursor-pointer"
                      onClick={() => { setViewBoard(b); setShowViewDialog(true); }}
                      data-testid={`board-thumb-${b.id}`}
                    />
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-900 truncate">{b.name}</p>
                      <p className="text-[10px] text-gray-400">{new Date(b.created_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={() => deleteBoard(b.id)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-board-${b.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Save dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Save Tactics Board</DialogTitle></DialogHeader>
            <Input
              placeholder="e.g. 4-3-3 Attacking Setup"
              value={boardName}
              onChange={(e) => setBoardName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSave()}
              data-testid="save-name-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-confirm-btn">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View board dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader><DialogTitle>{viewBoard?.name}</DialogTitle></DialogHeader>
            {viewBoard && (
              <div>
                <img src={viewBoard.image_data} alt={viewBoard.name} className="w-full rounded-lg" data-testid="view-board-image" />
                <div className="flex items-center justify-between mt-3">
                  <div className="flex gap-2">
                    <Badge variant="outline">{viewBoard.orientation}</Badge>
                    <Badge variant="outline">{viewBoard.pitch_style}</Badge>
                    <Badge variant="outline">{viewBoard.view_mode}</Badge>
                  </div>
                  <p className="text-xs text-gray-400">{new Date(viewBoard.created_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Unsaved changes dialog with Save option */}
      <AlertDialog open={showSavePrompt} onOpenChange={(open) => { if (!open) { cancelNavigation(); setShowSavePrompt(false); } }}>
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" strokeWidth={2} />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved drawings on your tactics board. Would you like to save before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 mt-4">
            <Button
              onClick={() => { setShowSavePrompt(false); cancelNavigation(); setShowSaveDialog(true); }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl"
              data-testid="unsaved-save-btn"
            >
              <Save className="w-4 h-4 mr-1" /> Save Board
            </Button>
            <Button
              variant="outline"
              onClick={() => { cancelNavigation(); setShowSavePrompt(false); }}
              className="w-full rounded-xl"
            >
              Stay on Page
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setShowSavePrompt(false); confirmNavigation(); }}
              className="w-full text-white bg-red-600 hover:bg-red-700 rounded-xl"
            >
              Leave Without Saving
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
